import FadeUp from "./FadeUp";
import { TrendingUp, DollarSign, Repeat, Percent, ArrowUpRight } from "lucide-react";

const metrics = [
    {
        icon: TrendingUp,
        value: "0 → 1,000+",
        label: "Ambassador community built from scratch",
    },
    {
        icon: DollarSign,
        value: "$0 → $300K",
        label: "Direct ARR from the creator channel",
    },
    {
        icon: ArrowUpRight,
        value: "$1M+",
        label: "ARR influenced across the funnel",
    },
    {
        icon: Percent,
        value: "-30%",
        label: "Blended CAC reduction",
    },
    {
        icon: Repeat,
        value: "+50%",
        label: "Trial → Paid conversion lift",
    },
];

export const CaseStudy = () => (
    <section
        id="case-study"
        data-testid="case-study-section"
        className="py-24 md:py-32 bg-[#0A0A0A] text-white overflow-hidden relative"
    >
        <div className="container-page">
            <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-14 lg:gap-16 items-start">
                <div>
                    <FadeUp>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-3 py-1.5">
                            <span className="text-[10.5px] font-bold tracking-[0.22em] uppercase text-white/70">
                                Case study
                            </span>
                            <span className="h-1 w-1 rounded-full bg-[#C6F432]" />
                            <span className="text-[11px] font-medium text-white/70">
                                Sunsama
                            </span>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <h2
                            data-testid="case-study-heading"
                            className="mt-6 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-white text-balance"
                        >
                            How we grew Sunsama's creator channel from $0 to $300K ARR.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <p className="mt-6 text-[15.5px] md:text-base text-white/70 leading-relaxed">
                            As Head of Partnerships at Sunsama, I built the ambassador and creator program from zero. It went from a side experiment to one of the leading acquisition channels, with a repeatable, in-house system running it.
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.15}>
                        <div className="mt-8 rounded-2xl bg-white/[0.05] border border-white/10 p-5 md:p-6">
                            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C6F432]">
                                My role
                            </p>
                            <p className="mt-2 text-[15px] text-white leading-[1.7]">
                                Head of Partnerships. Owned strategy, creator relationships, and affiliate program design.
                            </p>
                        </div>
                    </FadeUp>
                </div>

                {/* Right: metrics */}
                <div className="space-y-6">
                    <FadeUp delay={0.1}>
                        <div className="grid grid-cols-2 gap-3 md:gap-4">
                            {metrics.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`case-metric-${i}`}
                                    className={`rounded-2xl bg-white/[0.05] border border-white/10 p-5 md:p-6 ${
                                        i === 0 ? "col-span-2" : ""
                                    }`}
                                >
                                    <div className="flex items-center gap-2 text-white/50">
                                        <m.icon className="h-4 w-4" strokeWidth={1.8} />
                                        <span className="text-[11px] font-bold tracking-[0.15em] uppercase">
                                            Metric
                                        </span>
                                    </div>
                                    <div className="mt-3 font-display font-extrabold text-3xl md:text-4xl tracking-[-0.02em] text-white leading-[1.3]">
                                        {m.value}
                                    </div>
                                    <div className="mt-2.5 text-[13px] text-white/60 leading-[1.65]">
                                        {m.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.15}>
                        <div className="rounded-2xl bg-white/[0.05] border border-white/10 p-6">
                            <div className="flex items-center justify-between">
                                <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/60">
                                    ARR from creator channel
                                </p>
                                <span className="text-[11px] text-[#C6F432] font-semibold">
                                    +$300K ARR
                                </span>
                            </div>
                            <div className="mt-4 relative h-40">
                                <svg viewBox="0 0 400 160" className="w-full h-full" preserveAspectRatio="none">
                                    {[0, 1, 2, 3].map((i) => (
                                        <line
                                            key={i}
                                            x1="0"
                                            x2="400"
                                            y1={20 + i * 40}
                                            y2={20 + i * 40}
                                            stroke="rgba(255,255,255,0.06)"
                                            strokeWidth="1"
                                        />
                                    ))}
                                    <defs>
                                        <linearGradient id="fillG" x1="0" x2="0" y1="0" y2="1">
                                            <stop offset="0%" stopColor="#C6F432" stopOpacity="0.35" />
                                            <stop offset="100%" stopColor="#C6F432" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <path
                                        d="M 0 145 L 60 138 L 120 120 L 180 95 L 240 65 L 300 42 L 360 22 L 400 15 L 400 160 L 0 160 Z"
                                        fill="url(#fillG)"
                                    />
                                    <path
                                        d="M 0 145 L 60 138 L 120 120 L 180 95 L 240 65 L 300 42 L 360 22 L 400 15"
                                        fill="none"
                                        stroke="#C6F432"
                                        strokeWidth="2.5"
                                    />
                                    <circle cx="400" cy="15" r="5" fill="#C6F432" />
                                </svg>
                            </div>
                            <p className="mt-4 text-[13px] text-white/60 leading-relaxed">
                                From zero to $300K direct ARR, plus $1M+ influenced across the funnel.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </div>
    </section>
);

export default CaseStudy;
